/**
 * ── useTrialOrientation ──
 * Hook for managing trial orientation state including demo/clean mode.
 * Uses sessionStorage (not localStorage per security rules).
 */
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  type TrialOrientationState,
  type TrialMode,
  TRIAL_GOALS,
  ASSOCIATION_TRIAL_GOALS,
  getGoalsForTrialType,
  TRIAL_MILESTONES,
  getTrialOrientationState,
  setTrialOrientationState,
  initTrialOrientationState,
} from "@/lib/trialOrientation";

let listeners: Array<() => void> = [];

function emitChange() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot(): TrialOrientationState | null {
  return getTrialOrientationState();
}

export function useTrialOrientation() {
  const { user } = useAuth();
  const state = useSyncExternalStore(subscribe, getSnapshot);

  const isTrialUser = useMemo(() => {
    if (!user) return false;
    const meta = user.user_metadata;
    return meta?.signup_source === "free_trial";
  }, [user]);

  const needsOrientation = useMemo(() => {
    if (!isTrialUser) return false;
    if (!state) return true;
    return !state.orientationComplete;
  }, [isTrialUser, state]);

  const trialMode = state?.trialMode ?? null;
  const isDemoMode = trialMode === "demo" && !state?.demoPurged;

  const selectedGoal = useMemo(() => {
    if (!state?.selectedGoalId) return null;
    // Search both goal lists since the user may have switched context
    return TRIAL_GOALS.find((g) => g.id === state.selectedGoalId)
      ?? ASSOCIATION_TRIAL_GOALS.find((g) => g.id === state.selectedGoalId)
      ?? null;
  }, [state?.selectedGoalId]);

  const activationProgress = useMemo(() => {
    if (!state) return 0;
    const total = TRIAL_MILESTONES.length;
    const completed = state.completedMilestones.length;
    return Math.round((completed / total) * 100);
  }, [state]);

  const setTrialMode = useCallback((mode: TrialMode) => {
    const current = getTrialOrientationState() ?? initTrialOrientationState();
    const updated: TrialOrientationState = { ...current, trialMode: mode };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const selectGoal = useCallback((goalId: string) => {
    const current = getTrialOrientationState() ?? initTrialOrientationState();
    const updated: TrialOrientationState = {
      ...current,
      selectedGoalId: goalId,
      completedMilestones: current.completedMilestones.includes("goal_selected")
        ? current.completedMilestones
        : [...current.completedMilestones, "goal_selected"],
    };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const completeQuickWin = useCallback((key: string) => {
    const current = getTrialOrientationState();
    if (!current) return;
    if (current.completedQuickWins.includes(key)) return;
    const milestones = [...current.completedMilestones];
    if (!milestones.includes("first_action")) milestones.push("first_action");
    const updated: TrialOrientationState = {
      ...current,
      completedQuickWins: [...current.completedQuickWins, key],
      completedMilestones: milestones,
    };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const completeMilestone = useCallback((key: string) => {
    const current = getTrialOrientationState();
    if (!current) return;
    if (current.completedMilestones.includes(key)) return;
    const updated: TrialOrientationState = {
      ...current,
      completedMilestones: [...current.completedMilestones, key],
    };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const finishOrientation = useCallback(() => {
    const current = getTrialOrientationState();
    if (!current) return;
    const updated: TrialOrientationState = {
      ...current,
      orientationComplete: true,
    };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const markDemoPurged = useCallback(() => {
    const current = getTrialOrientationState();
    if (!current) return;
    const updated: TrialOrientationState = {
      ...current,
      demoPurged: true,
      trialMode: "clean",
    };
    setTrialOrientationState(updated);
    emitChange();
  }, []);

  const initOrientation = useCallback(() => {
    initTrialOrientationState();
    emitChange();
  }, []);

  return {
    isTrialUser,
    needsOrientation,
    state,
    trialMode,
    isDemoMode,
    selectedGoal,
    activationProgress,
    goals: TRIAL_GOALS,
    associationGoals: ASSOCIATION_TRIAL_GOALS,
    getGoalsForTrialType,
    milestones: TRIAL_MILESTONES,
    setTrialMode,
    selectGoal,
    completeQuickWin,
    completeMilestone,
    finishOrientation,
    markDemoPurged,
    initOrientation,
  };
}
